import React, { useState, useContext, useCallback, useEffect } from "react";
import { Upload, Alert, Radio, Spin } from "antd";
import { CloseOutlined, UploadOutlined } from "@ant-design/icons";
import { GlobalContext } from "contexts/GlobalContext";
import { PrimaryButton } from "components/Common/CustomButtons/CustomButtons";
import { toast } from "helpers/generalHelper";
import {
  TRADE_CAPTURE_SOURCE,
  TRADE_CAPTURE_SOURCE_OPTIONS,
} from "constants/tradeCapture";
import {
  getScreenshotFileError,
  isOtherCallPutImport,
  isOtherSurfaceOutput,
} from "helpers/tradeCaptureHelper";

const ACCEPT_TYPES = ["image/jpeg", "image/png", "image/webp"];

const BLOTTER_SCREENSHOT_TIPS = [
  "Take the screenshot at your normal screen size. Don't zoom in or out first.",
  "Capture the table edge to edge. Make sure no column on the right gets cut off.",
  "Leave a bit of empty space above the first row and below the last row. Don't slice through a row.",
  "Make sure no tooltips, menus, or pop-ups are covering any of the rows.",
  "Use your computer's screenshot tool (Cmd+Shift+4 on Mac, Snipping Tool / Win+Shift+S on Windows).",
];

const OTHER_CALL_PUT_SCREENSHOT_TIPS = [
  "Capture the Call/Put strike grid, including the window title ticker (TFO, EFO, UKA, …).",
  "Include the tenor on the Call/Put header (e.g. Oct26) and the Futures Last above the grid.",
  "Capture Bid and Offer (and their quantities) edge to edge. Don't cut off the right-hand columns.",
  "Leave a bit of empty space above the first row and below the last row. Don't slice through a row.",
  "Make sure no tooltips, menus, or pop-ups are covering any of the grid.",
];

const isAcceptedImage = (file) => file && ACCEPT_TYPES.includes(file.type);

function withPastedFileName(file) {
  if (file.name && !/^image\.(png|jpe?g|webp)$/i.test(file.name)) {
    return file;
  }
  const ext = file.type.split("/")[1]?.replace("jpeg", "jpg") || "png";
  return new File([file], `pasted-screenshot.${ext}`, { type: file.type });
}

function getPastedImageFile(clipboardData) {
  if (!clipboardData) return null;

  const { files, items } = clipboardData;

  if (files?.length) {
    for (let i = 0; i < files.length; i += 1) {
      const file = files[i];
      if (isAcceptedImage(file)) {
        return withPastedFileName(file);
      }
    }
  }

  if (!items?.length) return null;

  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];
    if (item.kind !== "file" || !item.type?.startsWith("image/")) continue;
    const file = item.getAsFile();
    if (isAcceptedImage(file)) {
      return withPastedFileName(file);
    }
  }

  return null;
}

const UploadForm = ({ onSubmit, loading, source, onSourceChange, output }) => {
  const {
    globalState: { loginInfo, dark_Theme },
  } = useContext(GlobalContext);

  const [fileList, setFileList] = useState([]);
  const [imageDimensions, setImageDimensions] = useState(null);
  // The Other surface fill reads the same Call/Put ladder, for a fixed
  // product and the caller's own surface, so there is no type or target.
  const isOtherSurface = isOtherSurfaceOutput(output);
  const isOtherCallPut = isOtherSurface || isOtherCallPutImport(source);
  const screenshotTips = isOtherCallPut
    ? OTHER_CALL_PUT_SCREENSHOT_TIPS
    : BLOTTER_SCREENSHOT_TIPS;

  const applyFile = useCallback((file) => {
    const fileError = getScreenshotFileError(file);
    if (fileError) {
      toast(fileError, "error");
      return;
    }
    if (isOtherCallPut) {
      // Ladder flows: each screenshot (one tenor) is added below the last.
      setFileList((prev) =>
        prev.some(
          (f) =>
            f.name === file.name &&
            f.size === file.size &&
            f.lastModified === file.lastModified
        )
          ? prev
          : [...prev, file]
      );
      return;
    }
    setFileList([file]);
    setImageDimensions(null);
    if (typeof file === "object" && file.type?.startsWith("image/")) {
      const url = URL.createObjectURL(file);
      const img = new window.Image();
      img.onload = () => {
        setImageDimensions({ width: img.naturalWidth, height: img.naturalHeight });
        URL.revokeObjectURL(url);
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
      };
      img.src = url;
    }
  }, [isOtherCallPut]);

  const handleFileChange = (info) => {
    if (loading) return;
    const { file } = info;
    applyFile(file);
  };

  const handlePaste = useCallback(
    (e) => {
      if (loading) return;
      const file = getPastedImageFile(e.clipboardData);
      if (!file) return;
      e.preventDefault();
      applyFile(file);
    },
    [applyFile, loading]
  );

  useEffect(() => {
    const handleDocumentPaste = (e) => {
      const tag = e.target?.tagName;
      if (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        e.target?.isContentEditable
      ) {
        return;
      }
      handlePaste(e);
    };

    document.addEventListener("paste", handleDocumentPaste);
    return () => document.removeEventListener("paste", handleDocumentPaste);
  }, [handlePaste]);

  useEffect(() => {
    if (!fileList.length) {
      setImageDimensions(null);
    }
  }, [fileList.length]);

  // The ICS blotter reads one screenshot; keep only the first when switching.
  useEffect(() => {
    if (!isOtherCallPut) {
      setFileList((prev) => (prev.length > 1 ? prev.slice(0, 1) : prev));
    }
  }, [isOtherCallPut]);

  const handleSubmit = () => {
    if (loading) return;
    if (!fileList.length) {
      toast("Please upload a screenshot", "error");
      return;
    }

    const formData = new FormData();
    formData.append("screenshot", fileList[0]);
    if (!isOtherSurface) {
      formData.append("target_user_id", loginInfo?.user_id);
    }

    onSubmit(formData, isOtherCallPut ? fileList.slice(1) : []);
  };

  return (
    <div
      className="trade-capture-upload"
      onPaste={handlePaste}
      tabIndex={-1}
    >
      <Spin
        spinning={loading}
        tip="OCR can take up to a minute. Please wait…"
      >
        <div className="trade-capture-upload__stack">
          {!isOtherSurface && (
          <div className="trade-capture-upload__source">
            <span
              className="trade-capture-upload__label"
              id="trade-capture-source-label"
            >
              Screenshot type
            </span>
            <Radio.Group
              aria-labelledby="trade-capture-source-label"
              className={`trade-capture-upload__source-switch ${
                dark_Theme ? "trade-capture-upload__source-switch--dark" : ""
              }`}
              value={source || TRADE_CAPTURE_SOURCE.ICS_BLOTTER}
              onChange={(e) => onSourceChange?.(e.target.value)}
              disabled={loading}
              optionType="button"
              buttonStyle="solid"
            >
              {TRADE_CAPTURE_SOURCE_OPTIONS.map((option) => (
                <Radio.Button key={option.value} value={option.value}>
                  {option.label}
                </Radio.Button>
              ))}
            </Radio.Group>
          </div>
          )}
          <div className="trade-capture-upload__section trade-capture-upload__section--upload">
            <label className="trade-capture-upload__label">
              {isOtherCallPut
                ? isOtherSurface
                  ? "Upload option chain screenshot for one tenor (JPEG, PNG, WebP — max 10MB) "
                  : "Upload Open Options screenshot (JPEG, PNG, WebP — max 10MB) "
                : "Upload Screenshot (JPEG, PNG, WebP — max 10MB) "}
              <span className="trade-capture-upload__required">*</span>
            </label>
            <p className="trade-capture-upload__hint">
              You can also paste an image from the clipboard (click here, then
              Ctrl+V / ⌘V).
            </p>
            <Upload.Dragger
              fileList={fileList}
              beforeUpload={() => false}
              onChange={handleFileChange}
              disabled={loading}
              onRemove={() => {
                setFileList([]);
                setImageDimensions(null);
              }}
              accept=".jpg,.jpeg,.png,.webp"
              multiple={isOtherCallPut}
              maxCount={isOtherCallPut ? undefined : 1}
              showUploadList={false}
              className={`trade-capture-upload__dragger ${
                dark_Theme ? "trade-capture-upload__dragger--dark" : ""
              }`}
            >
              <p
                className={`trade-capture-upload__dragger-icon ${
                  dark_Theme ? "trade-capture-upload__dragger-icon--dark" : ""
                }`}
              >
                <UploadOutlined />
              </p>
              <p
                className={`trade-capture-upload__dragger-text ${
                  dark_Theme ? "trade-capture-upload__dragger-text--dark" : ""
                }`}
              >
                {isOtherCallPut
                  ? "Click, drag, or paste screenshots here (Ctrl+V / ⌘V). Each one is added to the list."
                  : "Click, drag, or paste screenshot here (Ctrl+V / ⌘V)"}
              </p>
            </Upload.Dragger>
            {isOtherCallPut && fileList.length > 0 && (
              <ul className="trade-capture-upload__file-list">
                {fileList.map((file, index) => (
                  <li
                    key={`${file.name}-${file.size}-${file.lastModified}`}
                    className="trade-capture-upload__file-item"
                  >
                    <span className="trade-capture-upload__file-info">
                      {index + 1}. {file.name} ({(file.size / 1024).toFixed(1)} KB)
                    </span>
                    <button
                      type="button"
                      className="trade-capture-upload__file-remove"
                      aria-label={`Remove ${file.name}`}
                      title="Remove screenshot"
                      disabled={loading}
                      onClick={() =>
                        setFileList((prev) => prev.filter((_, i) => i !== index))
                      }
                    >
                      <CloseOutlined />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {!isOtherCallPut && fileList.length > 0 && (
              <span className="trade-capture-upload__file-info">
                {fileList[0].name} ({(fileList[0].size / 1024).toFixed(1)} KB)
                {imageDimensions
                  ? ` — ${imageDimensions.width}×${imageDimensions.height}px`
                  : ""}
              </span>
            )}
          </div>

          <Alert
            type="info"
            showIcon
            className="trade-capture-upload__tips-hint"
            message="Hint — tips for a clean screenshot"
            description={
              <ul className="trade-capture-upload__tips-list">
                {screenshotTips.map((tip) => (
                  <li key={tip}>{tip}</li>
                ))}
              </ul>
            }
          />
        </div>

        <div className="trade-capture-upload__actions">
          <PrimaryButton
            onClick={handleSubmit}
            loading={loading}
            disabled={!fileList.length || loading}
          >
            {loading
              ? isOtherSurface
                ? "Reading option chain..."
                : isOtherCallPut
                  ? "Extracting options..."
                  : "extracting trades..."
              : "Extract with AI"}
          </PrimaryButton>
        </div>
      </Spin>
    </div>
  );
};

export default UploadForm;
